import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchProjects, createProject, deleteProject, fetchActiveProjects, saveActiveProjects } from '../services/api';
import { getProjectId, getProjectName, getProjectKey } from '@shared/targetHelpers';
import { Button, Loading, Empty, ErrorText, errorMessage } from '../components/ui';
import { useMe } from '../hooks/useMe';

export default function ProjectSelectPage() {
  const navigate = useNavigate();
  const { isAdmin } = useMe();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newName, setNewName] = useState('');
  const [newNumber, setNewNumber] = useState('');
  const [creating, setCreating] = useState(false);
  // The create form is the rarer action on this page - it stays folded away
  // behind a single button so the list of projects is what the page is about.
  const [showCreate, setShowCreate] = useState(false);
  // The project queued for deletion, plus the name typed to confirm it. A
  // clicked-through confirmation is too easy to dismiss by muscle memory for
  // something this permanent, so the name has to be typed.
  const [pendingDelete, setPendingDelete] = useState(null);
  const [confirmName, setConfirmName] = useState('');
  const [deleting, setDeleting] = useState(false);
  // Only set when the tenant is OVER their plan's project limit, which is the
  // only time any of this appears. `null` is the normal case.
  const [quota, setQuota] = useState(null);
  const [choosing, setChoosing] = useState(false);
  const [picked, setPicked] = useState([]);
  const [savingPick, setSavingPick] = useState(false);

  async function load() {
    setError('');
    try {
      setProjects(await fetchProjects());
    } catch (err) {
      setError(errorMessage(err));
    }
    // Never fails the page. A tenant inside their plan gets restricted:false,
    // and an older API without this route 404s - both mean "nothing to show".
    try {
      const info = await fetchActiveProjects();
      setQuota(info?.restricted ? info : null);
      setPicked(info?.restricted ? (info.projects || []).filter((p) => p.writable).map((p) => p.projectId) : []);
    } catch {
      setQuota(null);
    }
  }

  useEffect(() => { setLoading(true); load().finally(() => setLoading(false)); }, []);

  // Which rows carry a lock. Empty unless over the limit.
  const writable = new Set((quota?.projects || []).filter((p) => p.writable).map((p) => p.projectId));
  const locked = (key) => Boolean(quota) && !writable.has(String(key || '').toLowerCase());

  function togglePick(projectId) {
    setPicked((current) => {
      if (current.includes(projectId)) return current.filter((id) => id !== projectId);
      // Stop at the limit rather than letting them select more and silently
      // dropping the overflow server-side.
      if (quota?.limit != null && current.length >= quota.limit) return current;
      return [...current, projectId];
    });
  }

  async function handleSavePick() {
    setSavingPick(true);
    setError('');
    try {
      await saveActiveProjects(picked);
      setChoosing(false);
      await load();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSavingPick(false);
    }
  }

  async function handleCreate() {
    if (!newName.trim()) return;
    setCreating(true);
    setError('');
    try {
      const project = await createProject(newName.trim(), newNumber.trim());
      setNewName('');
      setNewNumber('');
      navigate(`/projects/${encodeURIComponent(getProjectId(project))}`);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setCreating(false);
    }
  }

  async function handleDelete() {
    if (!pendingDelete) return;
    setDeleting(true);
    setError('');
    try {
      await deleteProject(pendingDelete.key);
      setPendingDelete(null);
      setConfirmName('');
      await load();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setDeleting(false);
    }
  }

  const confirmed = confirmName.trim().toLowerCase() === String(pendingDelete?.name || '').trim().toLowerCase();

  return (
    <>
      <div className="page-head">
        <h1>Projects</h1>
        {/* Creating a project is admin-only server-side; members work inside
            projects that already exist. */}
        {isAdmin && (
          <Button compact secondary onClick={() => setShowCreate((open) => !open)}>
            {showCreate ? 'Cancel' : 'New project'}
          </Button>
        )}
      </div>
      <p className="sub">Choose a project to upload to, review documents, or ask questions about.</p>
      <ErrorText>{error}</ErrorText>

      {/* Shown only while over the plan's project limit, which normally means
          just after a downgrade. Nothing is hidden or archived: every project
          below stays open, searchable and deletable. What is limited is how
          many can be CHANGED. */}
      {quota && (
        <div className="notice-warn">
          <div>
            <strong>
              Your plan covers {quota.limit} active project{quota.limit === 1 ? '' : 's'} and you have {quota.activeCount}.
            </strong>
            <div className="muted" style={{ marginTop: 4 }}>
              All of them stay readable, and you can still export or delete any of them. Only the{' '}
              {quota.limit} you choose can be edited or uploaded to.
            </div>
          </div>
          {isAdmin && (
            <div className="row" style={{ marginTop: 10 }}>
              <Button compact secondary onClick={() => setChoosing((open) => !open)}>
                {choosing ? 'Cancel' : 'Choose which'}
              </Button>
              {choosing && (
                <Button compact onClick={handleSavePick} disabled={savingPick || !picked.length}>
                  {savingPick ? 'Saving…' : `Save (${picked.length} of ${quota.limit})`}
                </Button>
              )}
            </div>
          )}
          {!isAdmin && (
            <div className="muted" style={{ marginTop: 8 }}>
              An owner or admin can change which projects are active.
            </div>
          )}
        </div>
      )}

      {showCreate && (
        <div className="inline-form">
          <div className="field field-grow">
            <label className="field-label" htmlFor="project-name">Project name</label>
            <input
              id="project-name"
              className="input"
              placeholder="e.g. Riverside Phase 2"
              value={newName}
              autoFocus
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
            />
          </div>
          <div className="field field-small">
            <label className="field-label" htmlFor="project-number">Number</label>
            <input
              id="project-number"
              className="input"
              placeholder="e.g. 24-118"
              value={newNumber}
              onChange={(e) => setNewNumber(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
            />
          </div>
          <Button compact onClick={handleCreate} disabled={!newName.trim() || creating}>
            {creating ? 'Creating…' : 'Create'}
          </Button>
        </div>
      )}

      {loading ? <Loading text="Loading projects…" rows={3} /> : projects.map((project, i) => {
        const number = project.projectNumber || '';
        const key = getProjectKey(project, i);
        const isLocked = locked(key);
        return (
          <div className="project-row" key={getProjectId(project) || i}>
            {/* In choose mode the checkbox leads the row. Outside the row
                button: clicking the row means "open the project". */}
            {choosing && isAdmin && (
              <input
                type="checkbox"
                className="project-pick"
                aria-label={`Keep ${getProjectName(project)} active`}
                checked={picked.includes(key)}
                onChange={() => togglePick(key)}
                // Nothing to untick down to - unticking the last one would
                // leave them unable to edit anything at all.
                disabled={!picked.includes(key) && quota?.limit != null && picked.length >= quota.limit}
              />
            )}
            <button
              type="button"
              className="list-row"
              onClick={() => navigate(`/projects/${encodeURIComponent(getProjectId(project))}`)}
            >
              <div className="list-body">
                <div className="list-title">
                  {getProjectName(project)}
                  {/* Read-only, not inaccessible - the project still opens. */}
                  {isLocked && <span className="badge warn" style={{ marginLeft: 8 }}>Read-only</span>}
                </div>
                {project.client && <div className="list-meta-muted">{project.client}</div>}
              </div>
              {number && <span className="project-number">{number}</span>}
            </button>
            {/* Outside the row button, not inside it: opening the project is
                what clicking the row means, and nested buttons are invalid
                markup anyway. */}
            {isAdmin && (
              <button
                type="button"
                className="row-delete"
                onClick={() => {
                  setPendingDelete({ key: getProjectKey(project, i), name: getProjectName(project) });
                  setConfirmName('');
                }}
              >
                Delete project
              </button>
            )}
          </div>
        );
      })}

      {pendingDelete && (
        <div className="modal-backdrop" role="dialog" aria-modal="true" aria-label="Delete project">
          <div className="modal-card">
            <h2 style={{ marginTop: 0 }}>Delete project</h2>
            <p className="muted">
              This permanently removes “{pendingDelete.name}” and its location structure. It cannot be undone.
            </p>
            <p className="muted">
              Documents already uploaded are kept in storage and are not deleted with the project.
            </p>
            <label className="field-label" htmlFor="confirm-project-name">
              Type the project name to confirm
            </label>
            <input
              id="confirm-project-name"
              className="input"
              placeholder={pendingDelete.name}
              value={confirmName}
              autoFocus
              onChange={(e) => setConfirmName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && confirmed && handleDelete()}
            />
            <div className="row" style={{ marginTop: 14 }}>
              <Button compact danger onClick={handleDelete} disabled={!confirmed || deleting}>
                {deleting ? 'Deleting…' : 'Delete project'}
              </Button>
              <Button
                compact
                secondary
                onClick={() => { setPendingDelete(null); setConfirmName(''); }}
                disabled={deleting}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
      {!loading && !projects.length && (
        <Empty icon="🗂️" title="No projects yet">Use “New project” above to create one.</Empty>
      )}
    </>
  );
}
